import { CSSProperties, RefObject, memo, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LottieRelativeAnimation, cn, type LottieRelativeAnimationProps } from '@bridebook/aura';

interface Props extends LottieRelativeAnimationProps {
  /**
   * When passed, animation is shown relative to this element, as if it was triggered from there.
   * If not provided, the animation will be positioned relative to the viewport.
   */
  sourceRef?: RefObject<HTMLElement | undefined>;
}

/**
 * Renders Lottie animation in a Portal
 */
const LottiePortalAnimationComp = ({ controller, sourceRef, ...props }: Props) => {
  const { isPlaying } = controller || {
    isPlaying: true, // Autoplay if controller is not provided
  };

  const [sourcePosition, setSourcePosition] = useState<CSSProperties | undefined>(undefined);

  /**
   * Update wrapper position and dimensions to match source element
   */
  const updateStyle = useCallback(() => {
    const source = sourceRef?.current;
    if (!source) return;
    const { width, height, top, left } = source.getBoundingClientRect();
    setSourcePosition({ width, height, top, left });
  }, [sourceRef]);

  /**
   * Update wrapper position when animation starts (only if sourceRef exists)
   */
  useEffect(() => {
    if (!isPlaying) {
      setSourcePosition(undefined);
      return;
    }
    if (sourceRef) {
      updateStyle();
    }
  }, [isPlaying, sourceRef, updateStyle]);

  /**
   * Observe source element resize and window scroll/resize
   * Only runs when sourceRef is provided
   */
  useEffect(() => {
    if (!isPlaying || !sourceRef) return;

    const source = sourceRef.current;
    if (!source) return;

    const resizeObserver = new ResizeObserver(updateStyle);
    resizeObserver.observe(source);

    window.addEventListener('resize', updateStyle);
    window.addEventListener('scroll', updateStyle, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateStyle);
      window.removeEventListener('scroll', updateStyle, true);
    };
  }, [isPlaying, sourceRef, updateStyle]);

  if (!isPlaying) return null;

  return createPortal(
    <div
      style={sourceRef && sourcePosition}
      className={cn('pointer-events-none fixed', !sourceRef && 'h-full w-full')}>
      <LottieRelativeAnimation {...props} />
    </div>,
    document.body,
  );
};

export const LottiePortalAnimation = memo(LottiePortalAnimationComp);
